// Pure logic of the Text Similarity Checker: breaks text into overlapping word-shingles (n-grams) and finds the
// shingles the two texts share. A genuine measure for two specific texts you provide; it is NOT a plagiarism
// checker, since it has no access to the web or any external database.

export const DEFAULT_THRESHOLD = 6;
/** Shared-phrase lengths offered (words). */
export const THRESHOLDS = [4, 5, 6, 7, 8] as const;

// Unicode-aware: letters and digits in any script count (the old \w-based version erased everything outside
// English). Chinese/Japanese text has no spaces, so each character is its own token.
const NOT_WORD = new RegExp("[^\\p{L}\\p{M}\\p{N}]+", "gu");
const CJK = new RegExp("[\\p{Script=Han}\\p{Script=Hiragana}\\p{Script=Katakana}]", "u");

export interface Token { show: string; norm: string; space: boolean }

export function tokenize(text: string): Token[] {
  const out: Token[] = [];
  for (const chunk of text.split(/\s+/).filter(Boolean)) {
    const chars = Array.from(chunk);
    if (chars.some((c) => CJK.test(c))) {
      let buf = "";
      const flush = () => { if (buf) { out.push({ show: buf, norm: buf.toLowerCase().replace(NOT_WORD, ""), space: false }); buf = ""; } };
      for (const c of chars) { if (CJK.test(c)) { flush(); out.push({ show: c, norm: c, space: false }); } else buf += c; }
      flush();
      if (out.length) out[out.length - 1].space = true;
    } else out.push({ show: chunk, norm: chunk.toLowerCase().replace(NOT_WORD, ""), space: true });
  }
  return out;
}

function shingleSet(norms: string[], size: number): Set<string> {
  const set = new Set<string>();
  for (let i = 0; i <= norms.length - size; i++) set.add(norms.slice(i, i + size).join(" "));
  return set;
}

/** Phrase length actually used: the chosen threshold (6 by default), shorter when a text has fewer words so short
 * texts (titles, taglines) can still be compared. Returns 0 when a text is too short to compare (under 3 words). */
export function effectiveShingleSize(wordsA: number, wordsB: number, threshold: number = DEFAULT_THRESHOLD): number {
  const shortest = Math.min(wordsA, wordsB);
  if (shortest < 3) return 0;
  return Math.min(threshold, shortest);
}

export function findMatchingSpans(textA: string, textB: string, sizeOverride?: number) {
  const tokensA = tokenize(textA);
  const normIdx = tokensA.map((t, i) => (t.norm ? i : -1)).filter((i) => i >= 0);
  const normsA = normIdx.map((i) => tokensA[i].norm);
  const normsB = tokenize(textB).map((t) => t.norm).filter(Boolean);
  const size = sizeOverride ?? DEFAULT_THRESHOLD;
  const shinglesB = shingleSet(normsB, size);
  const matched = new Set<number>();
  for (let i = 0; i <= normsA.length - size; i++) {
    if (shinglesB.has(normsA.slice(i, i + size).join(" "))) for (let j = i; j < i + size; j++) matched.add(normIdx[j]);
  }
  return { tokensA, matchedWordIndices: matched, countable: normsA.length, matchedCount: Array.from(matched).length, wordsB: normsB.length, size };
}

export interface Comparison {
  tokensA: Token[];
  tokensB: Token[];
  /** Token index to match number (0-based). The same phrase has the same number in both texts, which is what links the colours. */
  groupsA: Map<number, number>;
  groupsB: Map<number, number>;
  /** Share of each text's words that sit inside a shared phrase, 0-100. */
  percentA: number;
  percentB: number;
  matchCount: number;
  size: number;
  /** Either box has no words. */
  empty: boolean;
  /** Both boxes have words but one has under 3. */
  tooShort: boolean;
}

/** Everything the page shows, for both texts at once. An emptied box gives no groups at all (no stale highlights). */
export function compareTexts(textA: string, textB: string, threshold: number = DEFAULT_THRESHOLD): Comparison {
  const tokensA = tokenize(textA), tokensB = tokenize(textB);
  const idxA = tokensA.map((t, i) => (t.norm ? i : -1)).filter((i) => i >= 0);
  const idxB = tokensB.map((t, i) => (t.norm ? i : -1)).filter((i) => i >= 0);
  const normsA = idxA.map((i) => tokensA[i].norm), normsB = idxB.map((i) => tokensB[i].norm);
  const empty = normsA.length === 0 || normsB.length === 0;
  const size = empty ? 0 : effectiveShingleSize(normsA.length, normsB.length, threshold);
  const out: Comparison = { tokensA, tokensB, groupsA: new Map(), groupsB: new Map(), percentA: 0, percentB: 0, matchCount: 0, size, empty, tooShort: !empty && size === 0 };
  if (!size) return out;
  const setA = shingleSet(normsA, size), setB = shingleSet(normsB, size);
  const keyGroup = new Map<string, number>();
  const mark = (norms: string[], idx: number[], other: Set<string>, groups: Map<number, number>, create: boolean) => {
    for (let i = 0; i <= norms.length - size; i++) {
      const key = norms.slice(i, i + size).join(" ");
      if (!other.has(key)) continue;
      let g = keyGroup.get(key);
      if (g === undefined) { if (!create) continue; g = keyGroup.size; keyGroup.set(key, g); }
      for (let j = i; j < i + size; j++) if (!groups.has(idx[j])) groups.set(idx[j], g);
    }
  };
  mark(normsA, idxA, setB, out.groupsA, true);
  mark(normsB, idxB, setA, out.groupsB, false);
  out.percentA = (out.groupsA.size / normsA.length) * 100;
  out.percentB = (out.groupsB.size / normsB.length) * 100;
  out.matchCount = new Set(out.groupsA.values()).size;
  return out;
}
